import { AppInfoLoader, DeviceDetails, EnvironmentDetail, MasterIndirectTaskService, ToolHeartbeatService } from '@amzn/ihm-associate-tools-js-lib'
import { store } from '@atredux/store'
import { ITool, switchTool } from '@atredux/slices/toolSlice'
import { getStoresMatch } from '@atredux/selectors/storeSelector'
import { setMI } from '@atredux/slices/atAlertSlice'
import { setNotificationSheetOpen } from '@atredux/slices/notificationSlice'
import { pauseToolHeartbeat, resumeToolHeartbeat, setTool, closeHeartBeatMenu } from '@crossframe/apis/ToolMenu'
import { hideMigratedIFrame, switchToMigratedIFrame, switchToSeawardIFrame } from '@utils/ModalSwitchUtil'
import * as CandyCaneService from '@services/CandyCaneService'
import { HIDDEN_TOOLS, operationsTool, seawardReactTool, tasksTool } from '@src/services/ToolMenuConfigService'
import { IdleTimeService } from '@src/services/IdleTimeService'
import { ALL_TOOLS } from '@src/components/MIModal'
import * as BarcodeScannerService from '@services/barcode/BarcodeScannerService'
import { InventoryViewerModalUtils } from '@src/utils/InventoryViewerModalUtils'

export const _navigateToTool = (tool: ITool, toggleMenu?: () => void, toggleMenuButton?: () => void) => {
    const state = store.getState()
    const storesMatch = getStoresMatch(state)

    for (const toolId of HIDDEN_TOOLS) {
        if (tool.toolName === toolId) {
            tool.translateID = tool.displayNameStringId || tool.translateID
            if (!tool.translateID) {
                console.warn(`SMP: Missing translateID for hidden tool: ${tool.displayName}`)
            }
            tool.showMigrated = true
            break
        }
    }

    console.info('ATBase: Navigating to tool: ' + JSON.stringify(tool))

    // Close notifications sheet when navigating to a tool
    store.dispatch(setNotificationSheetOpen(false))

    // This switches back to the welcome page
    // displayName is used intentionally here: seawardReactTool has no toolName, and previousTool
    // is a spread copy so reference equality fails. displayName is stable for these built-in tools.
    if (tool.displayName === seawardReactTool.displayName) {
        console.info('ATBase: Initializing tool heartbeat service on platform...')
        AppInfoLoader.setToolName('None')
        ToolHeartbeatService.init()
        // P450576789: Dismiss camera scan button before switching to home (early return skips main path)
        BarcodeScannerService.stopScan()
        switchToMigratedIFrame()
        hideMigratedIFrame()
        store.dispatch(switchTool({ tool, storesMatch }))
        return
    }

    // Operations renders the in-portal Cloudscape tabs page (OperationsPage), not a tool
    // iframe. Mirror the Welcome/home path: stop any active scan, hide the tool iframe, and
    // switch the selected tool so ToolLayout renders OperationsPage in its place.
    if (tool.displayName === operationsTool.displayName) {
        console.info('ATBase: Navigating to Operations portal page...')
        AppInfoLoader.setToolName('None')
        ToolHeartbeatService.init()
        BarcodeScannerService.stopScan()
        switchToMigratedIFrame()
        hideMigratedIFrame()
        store.dispatch(switchTool({ tool, storesMatch }))
        return
    }

    if (tool.displayName === tasksTool.displayName) {
        console.info('ATBase: Initializing tool heartbeat service on platform...')
        AppInfoLoader.setToolName('None')
        ToolHeartbeatService.init()
        // P450576789: Dismiss camera scan button before switching to tasks (early return skips main path)
        BarcodeScannerService.stopScan()
        switchToMigratedIFrame()
        if (toggleMenuButton) {
            toggleMenuButton()
        }
        store.dispatch(switchTool({ tool, storesMatch }))
        return
    }

    const miConfig = MasterIndirectTaskService.getInstance().getConfig()
    const currentTask = MasterIndirectTaskService.getInstance().getMITask()

    if (miConfig && EnvironmentDetail.isAndroid()) {
        if (
            !miConfig.MasterIndirectTasksList.tasks[currentTask]?.directTasks.includes(tool.displayName) &&
            !miConfig.MasterIndirectTasksList.tasks[currentTask]?.directTasks.includes(ALL_TOOLS)
        ) {
            console.info(`Tool "${tool.displayName}" not in MI config, opening MI selection...`)
            store.dispatch(setMI())
            return
        } else {
            MasterIndirectTaskService.getInstance().setPureEvent(false)
        }
    }

    closeHeartBeatMenu()
    BarcodeScannerService.reset()
    // P450576789: Dismiss camera scan button — tool iframe is destroyed next and cannot send SCAN_STOP itself
    BarcodeScannerService.stopScan()

    if (tool.showMigrated) {
        pauseToolHeartbeat(true)
        switchToMigratedIFrame()
    } else {
        setTool(tool)
        resumeToolHeartbeat()
        switchToSeawardIFrame()
    }

    store.dispatch(switchTool({ tool, storesMatch }))

    if (miConfig && EnvironmentDetail.isAndroid()) {
        const promptThreshold = miConfig.MasterIndirectTasksList.tasks[currentTask]?.idlePromptThreshold
        const totThreshold = miConfig.MasterIndirectTasksList.tasks[currentTask]?.timeOffThreshold
        console.log('ATBase: Initializing idle service with parameters: ', promptThreshold, totThreshold)
        IdleTimeService.getInstance().init(promptThreshold, totThreshold)
    }

    if (toggleMenu) {
        toggleMenu()
    }

    if (toggleMenuButton) {
        toggleMenuButton()
    }

    if (tool.displayName === 'Inventory Viewer') {
        InventoryViewerModalUtils.sendIVEvent('openIVEvent', 'sidebar')
    }
}

export const fetchDeviceDetailsInfo = async (): Promise<{deviceId: string, appVersion: string}> => {
    let deviceId
    let appVersion
    try {
        const deviceDetails: {deviceSerialNumber?: string} = await DeviceDetails.getDeviceDetails()
        deviceId = deviceDetails.deviceSerialNumber
        appVersion = await CandyCaneService.getApplicationVersion()
    } catch (error) {
        console.error('ATBase: Something went wrong fetching deviceId or appVersion for menu footer: ' + error)
    }
    return {
        deviceId,
        appVersion,
    }
}
